import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { getAPIBaseURL } from '../services/helpers'
import { getData } from '../services/request'
import { getMissingMandatoryDocuments } from './ApplicationDocumentUpload'
import { applicationStatusMeta } from '../utils/employmeLabels'

export const statusMeta = applicationStatusMeta

const JobApplicationsPage = () => {
  const [applications, setApplications] = useState([])
  const [expandedId, setExpandedId] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    const fetchApplications = async () => {
      setLoading(true)
      const url = `${getAPIBaseURL()}/employme/jobseeker/applications`
      const { error: reqError, result } = await getData(url, {}, {})
      setLoading(false)
      if (reqError || !result) {
        setError('Unable to load applications')
        return
      }
      setApplications(result.data.data || [])
    }
    fetchApplications()
  }, [])

  const getInterviewSummary = (app) => {
    const status = (app.status || '').toString().toUpperCase().replace(/\s+/g, '_')
    if (status === 'INTERVIEW_IN_PROGRESS') return 'Interview in progress'
    if (status === 'SUBMITTED') return 'Interview submitted'
    if (status === 'UNDER_REVIEW') return 'Interview under review'
    if (status === 'SELECTED' || status === 'REJECTED' || status === 'EXPIRED') return 'Interview completed'
    return 'Interview not started yet'
  }

  return (
    <div className="mx-auto max-w-6xl">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-baseline sm:justify-between mb-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">My applications</h1>
          <p className="mt-1 text-sm text-slate-600">
            See where you are in each hiring process, from applied to selected or rejected.
          </p>
        </div>
      </div>

      {error && <div className="mb-4 text-sm text-red-600">{error}</div>}
      {loading && <div className="text-sm text-slate-600">Loading applications...</div>}
      {!loading && applications.length === 0 && (
        <div className="text-sm text-slate-600">
          You have not applied for any jobs yet. Browse open roles on the{' '}
          <Link to="/jobs" className="text-blue-700 hover:text-blue-800 underline">
            jobs page
          </Link>
          .
        </div>
      )}

      <div className="grid gap-4 mt-4">
        {applications.map((app) => {
          const meta = statusMeta(app.status)
          const normalizedStatus = (app.status || '').toString().toUpperCase().replace(/\s+/g, '_')
          const showResult = !!app.resultMessage
          const isExpanded = expandedId === app._id

          return (
            <div key={app._id} className="border rounded-lg p-4 bg-white shadow-sm">
              <div className="flex justify-between items-start gap-4">
                <div className="space-y-1">
                  <div className="text-sm font-medium text-slate-900">
                    {app.jobId?.title || 'Job'}
                  </div>
                  <div className="flex flex-wrap items-center gap-2 text-xs">
                    <span className={`inline-flex items-center rounded-full px-2.5 py-1 font-medium ${meta.color}`}>
                      {meta.label}
                    </span>
                    <span className="text-slate-500">
                      {getInterviewSummary(app)}
                    </span>
                  </div>
                </div>
                <div className="flex flex-col items-end gap-2">
                  <Link
                    to={`/jobs/${app.jobId?._id}`}
                    className="text-xs text-blue-700 hover:text-blue-800"
                  >
                    View job
                  </Link>
                  {(app.status === 'ELIGIBILITY_CONFIRMED' || app.status === 'INTERVIEW_IN_PROGRESS') && (
                    getMissingMandatoryDocuments(app.jobId, app).length > 0 ? (
                      <span className="text-xs text-amber-800">
                        Upload required documents on the job page
                      </span>
                    ) : (
                      <Link
                        to={`/jobs/applications/${app._id}/interview`}
                        className="text-xs text-amber-700 hover:text-amber-800"
                      >
                        {app.status === 'INTERVIEW_IN_PROGRESS' ? 'Continue interview' : 'Start interview'}
                      </Link>
                    )
                  )}
                  {showResult && (
                    <button
                      type="button"
                      onClick={() => setExpandedId(isExpanded ? null : app._id)}
                      className="mt-1 text-xs text-slate-700 hover:text-slate-900 underline"
                    >
                      {isExpanded ? 'Hide employer message' : 'View employer message'}
                    </button>
                  )}
                </div>
              </div>
              {showResult && isExpanded && (
                <div className="mt-3 rounded-md bg-slate-50 p-3 text-xs text-slate-800 whitespace-pre-line">
                  {app.resultMessage}
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default JobApplicationsPage

